import React, { useEffect, useMemo, useState } from "react";
import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { Box, CircularProgress } from "@mui/material";
import { Outlet } from "react-router-dom";
import msalConfig, { loginRequest } from "config/msal-config";
import AdminLogin from "./AdminLogin";
import RequestAccessPage from "./RequestAccessPage";
import { AdminIdentity, checkAdminAccess } from "utils/adminAccessApi";

type AccessState = "checking" | "authorized" | "unauthorized";

/**
 * Decodes JWT payload (without verifying signature) and reads token claims.
 */
function parseJwtClaims(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch (e) {
    console.error("Failed to decode JWT claims:", e);
    return null;
  }
}

/**
 * Claim mapping:
 *   name                   -> userName
 *   unique_name            -> emailId (fallback: preferred_username)
 *   https://bayer.com/cwid -> cwid
 *
 * Reads from access-token claims first and falls back to id-token claims.
 */
function resolveIdentity(
  accessTokenClaims: Record<string, unknown> | null,
  idTokenClaims: Record<string, unknown>
): AdminIdentity | null {
  const userName =
    (accessTokenClaims?.["name"] as string | undefined) ||
    (idTokenClaims["name"] as string | undefined);

  const emailId =
    (accessTokenClaims?.["unique_name"] as string | undefined) ||
    (idTokenClaims["unique_name"] as string | undefined) ||
    (idTokenClaims["preferred_username"] as string | undefined);

  const cwid =
    (accessTokenClaims?.["https://bayer.com/cwid"] as string | undefined) ||
    (idTokenClaims["https://bayer.com/cwid"] as string | undefined);

  if (!userName || !emailId || !cwid) {
    console.error("Missing required claims:", {
      userName,
      emailId,
      cwid,
      accessTokenHasClaims: !!accessTokenClaims,
    });
    return null;
  }

  return { userName, emailId, cwid };
}

function AdminAccessGuard() {
  const { instance } = useMsal();
  const [accessState, setAccessState] = useState<AccessState>("checking");
  const [identity, setIdentity] = useState<AdminIdentity | null>(null);

  useEffect(() => {
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) {
      setAccessState("unauthorized");
      return;
    }

    instance
      .acquireTokenSilent({ ...loginRequest, account })
      .then((tokenResponse) => {
        const accessTokenClaims = parseJwtClaims(tokenResponse.accessToken);
        const resolvedIdentity = resolveIdentity(
          accessTokenClaims,
          account.idTokenClaims as Record<string, unknown>
        );
        if (!resolvedIdentity) {
          setAccessState("unauthorized");
          return;
        }
        setIdentity(resolvedIdentity);
        return checkAdminAccess(resolvedIdentity.cwid);
      })
      .then((response) => {
        if (response) {
          setAccessState(response.hasAccess ? "authorized" : "unauthorized");
        }
      })
      .catch((err) => {
        console.error("Token acquisition or access check failed:", err);
        setAccessState("unauthorized");
      });
  }, [instance]);

  if (accessState === "checking") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (accessState === "unauthorized") {
    return <RequestAccessPage identity={identity ?? { userName: "", emailId: "", cwid: "" }} />;
  }

  return <Outlet />;
}

function AdminAppShell() {
  const [isMsalInitialized, setIsMsalInitialized] = useState(false);
  const msalInstance = useMemo(() => new PublicClientApplication(msalConfig), []);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();
        setIsMsalInitialized(true);
      } catch (error) {
        console.error("Failed to initialize MSAL:", error);
      }
    };
    initializeMsal();
  }, [msalInstance]);

  if (!isMsalInitialized) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthenticatedTemplate>
        <AdminAccessGuard />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <AdminLogin />
      </UnauthenticatedTemplate>
    </MsalProvider>
  );
}

export default AdminAppShell;
